import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Card,
  EmptyState,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuItemGroup,
  MenuItemGroupLabel,
  MenuPositioner,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  Pagination,
  SkeletonText,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react'
import {
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuListFilter,
  LuListStart,
  LuLoader,
} from 'react-icons/lu'
import type { SibValueChangeDto } from '../../../../api/dto/unionChanges.dto'
import type { UnionChangesCapabilities } from '../../../../api/unionChanges.api'
import { useUnionChanges } from '../hooks/useUnionChanges'
import {
  CHANGE_SORT_OPTIONS,
  CHANGE_TYPE_FILTERS,
  CHANGE_TYPE_LABEL,
  changeKey,
  filterChanges,
  formatChangeDate,
  formatChangeTimestamp,
  formatChangeValue,
  isFutureDate,
  PERIOD_OPTIONS,
  SEEN_FILTERS,
  sortChanges,
  type ChangeSortDir,
  type ChangeSortField,
  type ChangeTypeFilter,
  type PeriodPreset,
  type SeenFilter,
} from '../unionChanges.utils'

export interface UnionChangeTableProps {
  /** Per-SIB history: scopes every request to this SIB and drops the SIB column. */
  fixedSibUserId?: string
  /** The dashboard's view switcher, rendered in the toolbar so it stays next to the filters. */
  tabs?: ReactNode
  capabilities?: UnionChangesCapabilities
  /** The dashboard opens on unseen changes; the per-SIB history shows all of them. */
  initialSeenFilter?: SeenFilter
  initialPeriod?: PeriodPreset
  /** Background refresh runs only while the list is actually on screen. */
  enabled?: boolean
  pollIntervalMs?: number
}

/** Rows per page in the browser; the backend page size is a separate, larger number. */
export const UNION_CHANGE_ROWS_PER_PAGE = 20

const SKELETON_ROWS = 5

export const UNION_CHANGE_TABLE_LABEL = 'Änderungen an Preisen und Kapazitäten'

function MenuChoice({ label, selected }: { label: string; selected: boolean }) {
  return (
    <HStack justify="space-between" w="full">
      <Text>{label}</Text>
      {selected ? <LuCheck size={14} /> : null}
    </HStack>
  )
}

/**
 * Read-only list of the price and capacity changes SIBs saved, with the coordination group's
 * team-wide seen mark per row. Nothing here writes a price, a capacity or an effective date.
 *
 * The toolbar is the one the SIB and bank tables use: a search field, a sort menu and a filter
 * menu. They work on the rows loaded for the period, in the browser.
 */
export function UnionChangeTable({
  fixedSibUserId,
  tabs,
  capabilities,
  initialSeenFilter = 'unseen',
  initialPeriod,
  enabled = true,
  pollIntervalMs,
}: UnionChangeTableProps) {
  const list = useUnionChanges({
    fixedSibUserId,
    capabilities,
    initialPeriod,
    enabled,
    pollIntervalMs,
  })
  const { capabilities: caps } = list

  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<ChangeSortField>('changedAt')
  const [sortDir, setSortDir] = useState<ChangeSortDir>('desc')
  const [typeFilter, setTypeFilter] = useState<ChangeTypeFilter>('all')
  const [seenFilter, setSeenFilter] = useState<SeenFilter>(initialSeenFilter)
  const [page, setPage] = useState(1)

  // Any change of search, sort or filter starts again on the first page.
  useEffect(() => {
    setPage(1)
  }, [search, sortField, sortDir, typeFilter, seenFilter, list.period])

  const rows = useMemo(
    () =>
      sortChanges(
        filterChanges(list.items, { search, type: typeFilter, seen: seenFilter }),
        sortField,
        sortDir
      ),
    [list.items, search, typeFilter, seenFilter, sortField, sortDir]
  )

  const totalPages = Math.max(1, Math.ceil(rows.length / UNION_CHANGE_ROWS_PER_PAGE))
  // A refresh can shrink the list below the current page; stay on the last page that exists.
  const currentPage = Math.min(page, totalPages)
  const pageRows = rows.slice(
    (currentPage - 1) * UNION_CHANGE_ROWS_PER_PAGE,
    currentPage * UNION_CHANGE_ROWS_PER_PAGE
  )
  const rangeStart = rows.length === 0 ? 0 : (currentPage - 1) * UNION_CHANGE_ROWS_PER_PAGE + 1
  const rangeEnd = Math.min(currentPage * UNION_CHANGE_ROWS_PER_PAGE, rows.length)

  const filtersActive =
    typeFilter !== 'all' || seenFilter !== 'all' || list.period !== 'currentYear'

  const showSibColumn = !fixedSibUserId
  const columns = [
    ...(showSibColumn ? ['SIB'] : []),
    'Leistung',
    'Typ',
    'Alter Wert',
    'Neuer Wert',
    'Gültig ab',
    'Geändert am',
    'Geändert von',
    'Gesehen von / am',
    'Aktion',
  ]

  const emptyTitle =
    list.items.length === 0
      ? 'Keine Änderungen im gewählten Zeitraum.'
      : 'Keine Einträge gefunden.'
  const emptyDescription =
    list.items.length === 0
      ? 'Passen Sie den Zeitraum im Filter an.'
      : 'Passen Sie Suche oder Filter an.'

  return (
    <Stack gap={4} data-testid="union-change-table">
      {/* Toolbar */}
      <HStack w="full" gap={3} align="center" flexWrap="wrap">
        <Input
          flex="1"
          minW="240px"
          placeholder="Änderung suchen, SIB, Leistung oder Name eingeben"
          aria-label="Änderung suchen"
          value={search}
          onChange={e => setSearch(e.target.value)}
          disabled={list.loading}
        />

        {tabs}

        {/* Sort */}
        <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <MenuTrigger asChild>
            <Button variant="subtle" size="sm">
              <HStack gap={2}>
                <LuListStart />
                <Text>Sortierung</Text>
              </HStack>
            </Button>
          </MenuTrigger>
          <MenuPositioner>
            <MenuContent minW="220px" bg="bg" borderColor="border">
              {CHANGE_SORT_OPTIONS.map(opt => (
                <MenuItem
                  key={opt.value}
                  value={`sort:${opt.value}`}
                  onClick={() => setSortField(opt.value)}
                >
                  <MenuChoice label={opt.label} selected={sortField === opt.value} />
                </MenuItem>
              ))}
              <MenuSeparator />
              <MenuItem value="dir:asc" onClick={() => setSortDir('asc')}>
                <MenuChoice label="Aufsteigend" selected={sortDir === 'asc'} />
              </MenuItem>
              <MenuItem value="dir:desc" onClick={() => setSortDir('desc')}>
                <MenuChoice label="Absteigend" selected={sortDir === 'desc'} />
              </MenuItem>
            </MenuContent>
          </MenuPositioner>
        </MenuRoot>

        {/* Filter */}
        <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <MenuTrigger asChild>
            <Button
              variant={filtersActive ? 'solid' : 'subtle'}
              colorPalette={filtersActive ? 'teal' : 'gray'}
              size="sm"
            >
              <HStack gap={2}>
                <LuListFilter />
                <Text>Filter</Text>
              </HStack>
            </Button>
          </MenuTrigger>
          <MenuPositioner>
            <MenuContent minW="240px" bg="bg" borderColor="border">
              <MenuItemGroup>
                <MenuItemGroupLabel>Zeitraum</MenuItemGroupLabel>
                {PERIOD_OPTIONS.map(opt => (
                  <MenuItem
                    key={opt.value}
                    value={`period:${opt.value}`}
                    onClick={() => list.setPeriod(opt.value)}
                  >
                    <MenuChoice label={opt.label} selected={list.period === opt.value} />
                  </MenuItem>
                ))}
              </MenuItemGroup>
              <MenuSeparator />
              <MenuItemGroup>
                <MenuItemGroupLabel>Typ</MenuItemGroupLabel>
                {CHANGE_TYPE_FILTERS.map(opt => (
                  <MenuItem
                    key={opt.value}
                    value={`type:${opt.value}`}
                    onClick={() => setTypeFilter(opt.value)}
                  >
                    <MenuChoice label={opt.label} selected={typeFilter === opt.value} />
                  </MenuItem>
                ))}
              </MenuItemGroup>
              <MenuSeparator />
              <MenuItemGroup>
                <MenuItemGroupLabel>Anzeige</MenuItemGroupLabel>
                {SEEN_FILTERS.map(opt => (
                  <MenuItem
                    key={opt.value}
                    value={`seen:${opt.value}`}
                    onClick={() => setSeenFilter(opt.value)}
                  >
                    <MenuChoice label={opt.label} selected={seenFilter === opt.value} />
                  </MenuItem>
                ))}
              </MenuItemGroup>
            </MenuContent>
          </MenuPositioner>
        </MenuRoot>
      </HStack>

      {list.error ? (
        <Alert.Root status="error" variant="subtle">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Fehler beim Laden</Alert.Title>
            <Alert.Description>{list.error}</Alert.Description>
          </Alert.Content>
          <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
            Erneut versuchen
          </Button>
        </Alert.Root>
      ) : (
        <Card.Root variant="outline" overflow="hidden">
          {list.loadingMore && (
            <HStack
              px={4}
              py={2}
              gap={2}
              bg="blue.subtle"
              borderBottomWidth="1px"
              borderColor="blue.muted"
              data-testid="union-change-loading-more"
            >
              <Box color="blue.fg" flexShrink={0}>
                <LuLoader size={13} />
              </Box>
              <Text fontSize="xs" color="blue.fg">
                Noch nicht alle Daten geladen – Suche läuft auf {list.items.length} von{' '}
                {list.totalCount} Änderungen. Weitere Ergebnisse erscheinen automatisch.
              </Text>
            </HStack>
          )}
          <Table.ScrollArea>
            <Table.Root
              native
              minW="1100px"
              aria-label={UNION_CHANGE_TABLE_LABEL}
              aria-busy={list.loading || list.loadingMore || list.refreshing}
            >
              <Table.Header>
                <Table.Row>
                  {columns.map(column => (
                    <Table.ColumnHeader key={column} whiteSpace="nowrap">
                      {column}
                    </Table.ColumnHeader>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {list.loading ? (
                  Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
                    <Table.Row key={`skeleton-${rowIndex}`} data-testid="union-change-skeleton">
                      {columns.map(column => (
                        <Table.Cell key={column}>
                          <SkeletonText noOfLines={1} />
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))
                ) : pageRows.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={columns.length}>
                      <EmptyState.Root>
                        <EmptyState.Content>
                          <EmptyState.Title>{emptyTitle}</EmptyState.Title>
                          <EmptyState.Description>{emptyDescription}</EmptyState.Description>
                        </EmptyState.Content>
                      </EmptyState.Root>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  pageRows.map(change => (
                    <ChangeRow
                      key={changeKey(change)}
                      change={change}
                      showSibColumn={showSibColumn}
                      undoAvailable={caps.undoSeen}
                      pending={list.pendingKeys.has(changeKey(change))}
                      onMarkSeen={() => void list.markSeen(change)}
                      onUndoSeen={() => void list.undoSeen(change)}
                    />
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>

          {!list.loading && rows.length > 0 && (
            <Card.Footer justifyContent="space-between" flexWrap="wrap" gap={3}>
              <Text color="fg.muted">
                {rangeStart}–{rangeEnd} von {rows.length} Änderungen
              </Text>
              {rows.length > UNION_CHANGE_ROWS_PER_PAGE && (
                <Pagination.Root
                  count={rows.length}
                  pageSize={UNION_CHANGE_ROWS_PER_PAGE}
                  page={currentPage}
                  onPageChange={details => setPage(details.page)}
                >
                  <ButtonGroup variant="ghost" size="sm">
                    <Pagination.PrevTrigger asChild>
                      <IconButton aria-label="Vorherige Seite">
                        <LuChevronLeft />
                      </IconButton>
                    </Pagination.PrevTrigger>
                    <Pagination.Items
                      render={pageItem => (
                        <IconButton
                          aria-label={`Seite ${pageItem.value}`}
                          variant={{ base: 'ghost', _selected: 'outline' }}
                        >
                          {pageItem.value}
                        </IconButton>
                      )}
                    />
                    <Pagination.NextTrigger asChild>
                      <IconButton aria-label="Nächste Seite">
                        <LuChevronRight />
                      </IconButton>
                    </Pagination.NextTrigger>
                  </ButtonGroup>
                </Pagination.Root>
              )}
            </Card.Footer>
          )}
        </Card.Root>
      )}
    </Stack>
  )
}

function ChangeRow({
  change,
  showSibColumn,
  undoAvailable,
  pending,
  onMarkSeen,
  onUndoSeen,
}: {
  change: SibValueChangeDto
  showSibColumn: boolean
  undoAvailable: boolean
  pending: boolean
  onMarkSeen: () => void
  onUndoSeen: () => void
}) {
  const key = changeKey(change)
  const planned = isFutureDate(change.validFrom)

  return (
    <Table.Row data-testid={`union-change-row-${key}`}>
      {showSibColumn && <Table.Cell whiteSpace="nowrap">{change.sibName}</Table.Cell>}
      <Table.Cell whiteSpace="nowrap">{change.serviceName || change.serviceCode}</Table.Cell>
      <Table.Cell>
        <Badge colorPalette="gray" variant="subtle">
          {CHANGE_TYPE_LABEL[change.changeType]}
        </Badge>
      </Table.Cell>
      <Table.Cell whiteSpace="nowrap" data-testid="union-change-old-value">
        {formatChangeValue(change.oldValue, change.changeType, change.serviceCode)}
      </Table.Cell>
      <Table.Cell whiteSpace="nowrap" data-testid="union-change-new-value">
        {formatChangeValue(change.newValue, change.changeType, change.serviceCode)}
      </Table.Cell>
      <Table.Cell whiteSpace="nowrap">
        <HStack gap={2}>
          <Text as="span">{formatChangeDate(change.validFrom)}</Text>
          {planned && (
            <Badge colorPalette="gray" variant="outline">
              geplant
            </Badge>
          )}
        </HStack>
      </Table.Cell>
      <Table.Cell whiteSpace="nowrap">{formatChangeTimestamp(change.changedAt)}</Table.Cell>
      <Table.Cell whiteSpace="nowrap">{change.changedByName || change.changedBy}</Table.Cell>
      <Table.Cell whiteSpace="nowrap" data-testid="union-change-seen">
        {change.seen ? (
          <Stack gap={0}>
            <Text>{change.seen.seenByName || change.seen.seenBy}</Text>
            <Text color="fg.muted">{formatChangeTimestamp(change.seen.seenAt)}</Text>
          </Stack>
        ) : (
          <Text color="fg.muted">Noch nicht gesehen</Text>
        )}
      </Table.Cell>
      <Table.Cell whiteSpace="nowrap" textAlign="end">
        {change.seen ? (
          undoAvailable ? (
            <Button size="sm" variant="ghost" loading={pending} onClick={onUndoSeen}>
              Gesehen-Markierung entfernen
            </Button>
          ) : null
        ) : (
          <Button size="sm" variant="outline" loading={pending} onClick={onMarkSeen}>
            Als gesehen markieren
          </Button>
        )}
      </Table.Cell>
    </Table.Row>
  )
}
